/**
 * @fileoverview Grid of live legacy schema tables with manifest inclusion controls.
 * @module system-web/components/migration/manifest-editor/ManifestTablesGrid
 *
 * Responsibilities:
 * - Render each base table from INFORMATION_SCHEMA with its role in the dataset manifest.
 * - Allow administrators to exclude or re-include tables via a toggle.
 * - Show column count fetched on-demand when a row is expanded.
 *
 * Dependencies:
 * - lucide-react (icons)
 * - legacyManifestService (SchemaTable, SchemaColumn)
 * - useLegacySchema (fetchColumnsFor, columnsCache)
 *
 * Architecture:
 * - Presentation + local state only; exclude/include decisions are bubbled up to the parent
 *   via onExcludeChange and reflected in the excludedTables list.
 * - Column details are fetched imperatively from useLegacySchema.fetchColumnsFor when
 *   a row is expanded — never inside liveQuery.
 *
 * Notes:
 * - Tables that are the primaryTable of the manifest display a "Primary" role badge.
 * - Tables listed in source.childTables display a "Child" badge.
 * - All other live tables show an "Extra" badge (not in manifest baseline).
 * - Excluded tables are visually dimmed.
 *
 * @reusable
 * Usage: Rendered inside ManifestEditorTab next to the relationships grid.
 * Rules: stateful (local expand/loading state); receives schema data + override state from parent.
 */

import React, { useState, useCallback } from 'react';
import {
  ChevronDown, ChevronRight, EyeOff, Eye, Loader2, AlertTriangle, Table2,
} from 'lucide-react';
import type { SchemaTable, SchemaColumn } from '../../../services/legacyManifestService';

// ---------------------------------------------------------------------------
// Contracts
// ---------------------------------------------------------------------------

/**
 * @contract TableRole
 * Purpose:  The role of a live schema table relative to the dataset manifest.
 * Fields:   'primary' | 'child' | 'extra'
 * Rules:    Derived from the baseline manifest on render; not persisted.
 * Sync:     Not applicable.
 */
export type TableRole = 'primary' | 'child' | 'extra';

/**
 * @contract ManifestTablesGridProps
 *
 * Purpose:
 * - Props for the ManifestTablesGrid component.
 *
 * Fields:
 * - tables: All base tables from INFORMATION_SCHEMA for the selected legacy DB.
 * - primaryTable: The baseline manifest primaryTable name.
 * - childTableNames: Set of baseline childTable names.
 * - excludedTables: Currently excluded table names from the active override.
 * - isLoading: True while the live table list is fetching.
 * - error: Non-null when the table fetch failed.
 * - onExcludeChange: Callback when a table's excluded status changes.
 * - fetchColumnsFor: Async function to fetch columns for a table (from useLegacySchema).
 * - columnsCache: Map of table name → SchemaColumn[] (from useLegacySchema).
 *
 * Rules:
 * - primaryTable cannot be excluded (toggle is disabled for it).
 *
 * Sync:
 * - Tables are online-exception; not persisted to Dexie.
 */
export interface ManifestTablesGridProps {
  tables: SchemaTable[];
  primaryTable: string;
  childTableNames: Set<string>;
  excludedTables: string[];
  isLoading: boolean;
  error: string | null;
  onExcludeChange: (table: string, excluded: boolean) => void;
  fetchColumnsFor: (table: string) => Promise<SchemaColumn[]>;
  columnsCache: Map<string, SchemaColumn[]>;
}

// ---------------------------------------------------------------------------
// Role badge
// ---------------------------------------------------------------------------

const ROLE_CONFIG: Record<TableRole, { label: string; bg: string; color: string }> = {
  primary: { label: 'Primary', bg: 'rgba(37,99,235,0.1)', color: '#1d4ed8' },
  child:   { label: 'Child',   bg: 'rgba(22,163,74,0.1)', color: '#15803d' },
  extra:   { label: 'Extra',   bg: 'rgba(107,114,128,0.1)', color: '#374151' },
};

function RoleBadge({ role }: { role: TableRole }) {
  const cfg = ROLE_CONFIG[role];
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium"
      style={{ background: cfg.bg, color: cfg.color }}
    >
      {cfg.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Single table row (expanded/collapsed)
// ---------------------------------------------------------------------------

interface TableRowProps {
  table: SchemaTable;
  role: TableRole;
  isExcluded: boolean;
  isPrimary: boolean;
  onToggleExclude: () => void;
  fetchColumnsFor: (table: string) => Promise<SchemaColumn[]>;
  columnsCache: Map<string, SchemaColumn[]>;
}

function TableRow({
  table,
  role,
  isExcluded,
  isPrimary,
  onToggleExclude,
  fetchColumnsFor,
  columnsCache,
}: TableRowProps) {
  const [expanded, setExpanded] = useState(false);
  const [isLoadingCols, setIsLoadingCols] = useState(false);
  const [colError, setColError] = useState<string | null>(null);

  const tableName = table.table;
  const columns = columnsCache.get(tableName);

  const handleExpand = useCallback(async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !columnsCache.has(tableName)) {
      setIsLoadingCols(true);
      setColError(null);
      try {
        await fetchColumnsFor(tableName);
      } catch (err) {
        setColError(err instanceof Error ? err.message : 'Could not load columns');
      } finally {
        setIsLoadingCols(false);
      }
    }
  }, [expanded, columnsCache, tableName, fetchColumnsFor]);

  const colCount = columns?.length;

  return (
    <>
      <tr
        className="border-b transition-colors hover:bg-gray-50"
        style={{
          borderColor: 'rgba(0,0,0,0.06)',
          opacity: isExcluded ? 0.45 : 1,
        }}
      >
        {/* Expand toggle */}
        <td className="px-2 py-2 w-8">
          <button
            onClick={handleExpand}
            className="p-0.5 rounded hover:bg-gray-100 text-gray-400"
            aria-label={expanded ? 'Collapse columns' : 'Expand columns'}
          >
            {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </button>
        </td>

        {/* Table name */}
        <td className="px-2 py-2">
          <div className="flex items-center gap-1.5">
            <Table2 className="h-3.5 w-3.5 text-gray-400 shrink-0" />
            <span className="font-mono text-xs">{table.schema}.{tableName}</span>
            {isExcluded && (
              <span className="text-xs text-gray-400 italic">(excluded)</span>
            )}
          </div>
        </td>

        {/* Column count */}
        <td className="px-2 py-2 text-right text-xs text-gray-500 w-20">
          {colCount != null ? `${colCount} col${colCount !== 1 ? 's' : ''}` : <span className="opacity-30">—</span>}
        </td>

        {/* Role badge */}
        <td className="px-2 py-2 w-20">
          <RoleBadge role={role} />
        </td>

        {/* Exclude / Include toggle */}
        <td className="px-2 py-2 w-24 text-right">
          <button
            onClick={onToggleExclude}
            disabled={isPrimary}
            title={isPrimary ? 'The primary table cannot be excluded' : isExcluded ? 'Re-include this table' : 'Exclude this table'}
            className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs border hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            {isExcluded ? (
              <>
                <Eye className="h-3 w-3" />
                Include
              </>
            ) : (
              <>
                <EyeOff className="h-3 w-3" />
                Exclude
              </>
            )}
          </button>
        </td>
      </tr>

      {/* Expanded columns panel */}
      {expanded && (
        <tr className="bg-gray-50" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
          <td />
          <td colSpan={4} className="px-4 pb-3 pt-1">
            {isLoadingCols && (
              <div className="flex items-center gap-2 text-xs text-gray-500 py-2">
                <Loader2 className="h-3 w-3 animate-spin" />
                Loading columns…
              </div>
            )}
            {colError && (
              <div className="flex items-center gap-1.5 text-xs text-red-600 py-2">
                <AlertTriangle className="h-3 w-3" />
                {colError}
              </div>
            )}
            {!isLoadingCols && !colError && columns && columns.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {columns.map((col) => (
                  <span
                    key={col.column}
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-mono"
                    style={{
                      background: col.isPrimaryKey ? 'rgba(37,99,235,0.1)' : 'rgba(107,114,128,0.08)',
                      color: col.isPrimaryKey ? '#1d4ed8' : '#374151',
                    }}
                    title={`${col.dataType} | nullable: ${col.isNullable}`}
                  >
                    {col.isPrimaryKey && <span className="font-bold">PK</span>}
                    {col.column}
                    <span className="opacity-50 text-[10px]">{col.dataType}</span>
                  </span>
                ))}
              </div>
            )}
            {!isLoadingCols && !colError && columns && columns.length === 0 && (
              <p className="text-xs text-gray-400 py-1">No columns found.</p>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * @component ManifestTablesGrid
 *
 * Purpose:
 * - Display all INFORMATION_SCHEMA base tables for the selected legacy database, with
 *   roles derived from the baseline manifest and exclude/include controls for the override.
 *
 * Props:
 * - See ManifestTablesGridProps.
 *
 * Behavior:
 * - Shows spinner while tables are loading.
 * - Shows error banner if table fetch failed.
 * - Each row has an expand button to reveal column details (fetched on demand).
 * - Role badge indicates Primary / Child / Extra status.
 * - Exclude/Include toggle calls onExcludeChange (bubbles to parent override state).
 * - Primary table toggle is disabled.
 *
 * Data Source:
 * - remote (online-exception INFORMATION_SCHEMA via useLegacySchema)
 *
 * Constraints:
 * - No Dexie reads or writes.
 * - All mutation decisions are controlled by the parent via onExcludeChange.
 *
 * Performance:
 * - Column details are fetched and cached lazily on first expand.
 *
 * @reusable
 * Usage: One per active dataset inside ManifestEditorTab.
 * Rules: stateful (expand / column-loading local state); props-driven for exclude decisions.
 */
export function ManifestTablesGrid({
  tables,
  primaryTable,
  childTableNames,
  excludedTables,
  isLoading,
  error,
  onExcludeChange,
  fetchColumnsFor,
  columnsCache,
}: ManifestTablesGridProps) {
  const excludedSet = new Set(excludedTables);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-6 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading schema tables…
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        <span>{error}</span>
      </div>
    );
  }

  if (tables.length === 0) {
    return (
      <p className="text-sm text-gray-400 py-4 text-center">
        No tables loaded — click <strong>Load Schema</strong> above to fetch INFORMATION_SCHEMA.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b bg-gray-50" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
            <th className="w-8 px-2 py-2" />
            <th className="px-2 py-2 text-left text-xs font-semibold uppercase tracking-wide opacity-60">Table</th>
            <th className="px-2 py-2 text-right text-xs font-semibold uppercase tracking-wide opacity-60 w-20">Columns</th>
            <th className="px-2 py-2 text-left text-xs font-semibold uppercase tracking-wide opacity-60 w-20">Role</th>
            <th className="px-2 py-2 w-24" />
          </tr>
        </thead>
        <tbody>
          {tables.map((t) => {
            const role: TableRole =
              t.table === primaryTable ? 'primary' :
              childTableNames.has(t.table) ? 'child' : 'extra';
            return (
              <TableRow
                key={`${t.schema}.${t.table}`}
                table={t}
                role={role}
                isExcluded={excludedSet.has(t.table)}
                isPrimary={t.table === primaryTable}
                onToggleExclude={() => onExcludeChange(t.table, !excludedSet.has(t.table))}
                fetchColumnsFor={fetchColumnsFor}
                columnsCache={columnsCache}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
